import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

// Get API configuration from environment
const getApiConfig = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const wsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
  
  return {
    apiUrl,
    wsUrl,
    isProduction: process.env.REACT_APP_ENVIRONMENT === 'production'
  };
};

const config = getApiConfig();

const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track refresh attempts to prevent multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle unified response structure and token refresh
api.interceptors.response.use(
  (response) => {
    // Handle unified response structure
    const apiResponse = response.data;
    
    // Check if response follows unified structure
    if (apiResponse && typeof apiResponse === 'object' && 'success' in apiResponse) {
      if (!apiResponse.success) {
        // Convert to error - create an error object that will be caught by error handler
        const error = new Error(apiResponse.error?.message || 'Request failed');
        error.response = {
          ...response,
          data: apiResponse,
        };
        error.isApiError = true;
        error.apiError = apiResponse.error;
        return Promise.reject(error);
      }
      
      // Success case: return the data property
      return {
        ...response,
        data: apiResponse.data,
      };
    }
    
    // Legacy response format (no unified structure) - return as is
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't retry refresh token requests, login, register, logout, or password reset requests to prevent infinite loops
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                          originalRequest.url?.includes('/auth/register') || 
                          originalRequest.url?.includes('/auth/logout') ||
                          originalRequest.url?.includes('/auth/password-reset') ||
                          originalRequest.url?.includes('/refresh-token');
    
    if (error.response?.status === 401 && !originalRequest._retry && (originalRequest._retryCount || 0) < 2 && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      isRefreshing = true;

      try {
        console.log('Attempting token refresh...');
        const success = await useAuthStore.getState().refreshAuthToken();
        if (success) {
          const { accessToken } = useAuthStore.getState();
          console.log('Token refresh successful');
          processQueue(null, accessToken);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } else {
          console.log('Token refresh failed');
          processQueue(new Error('Refresh failed'), null);

          // Only logout if this is not during initial auth check and not an auth endpoint
          if (!originalRequest.url?.includes('/auth/me') && !isAuthEndpoint) {
            useAuthStore.getState().logout();
          }

          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        processQueue(refreshError, null);
        // Only logout if this is not during initial auth check and not an auth endpoint
        if (!originalRequest.url?.includes('/auth/me') && !isAuthEndpoint) {
          useAuthStore.getState().logout();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle unified error structure in error responses
    if (error.response?.data) {
      const responseData = error.response.data;
      if (responseData && typeof responseData === 'object' && 'success' in responseData) {
        if (!responseData.success && responseData.error) {
          // Create error with unified structure
          const apiError = new Error(responseData.error.message || 'Request failed');
          apiError.response = {
            ...error.response,
            data: responseData,
          };
          apiError.isApiError = true;
          apiError.apiError = responseData.error;
          return Promise.reject(apiError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
export { config as apiConfig }; 